import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Copy,
  Italic,
  Minus,
  Plus,
  Trash2,
  Underline,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  TextOverlay,
  TextRun,
  TextRunStyle,
  normalizeTextRuns,
} from "../models/Post";
import { RichTextField } from "./RichTextField";
import { useAuth } from "../contexts/AuthContext";
import { getAvailableFontsForUser, FONT_SIZES } from "../utils/textOverlayUtils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Slider } from "./ui/slider";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import {
  BrandColorPickerSwatch,
  type BrandKitColorPickerContext,
  type BrandColorShortcut,
} from "./BrandColorShortcuts";
import { HexColorInput } from "./HexColorInput";

// CSS to hide number input spinners
const hideNumberInputSpinners = `
  /* For Webkit browsers like Chrome, Safari */
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  /* For Firefox */
  input[type=number] {
    -moz-appearance: textfield;
  }
`;

// Text style presets
const TEXT_PRESETS: Array<{
  name: string;
  style: Omit<
    TextOverlay["style"],
    "fontFamily" | "fontSize" | "color" | "backgroundColor"
  > & {
    fontFamily: string;
    fontSize: number;
    color: string;
    backgroundColor: string;
  };
  text: string;
  previewImage?: string;
}> = [
  {
    name: "Headline",
    style: {
      fontFamily: "Impact",
      fontSize: 40,
      color: "#FFFFFF",
      backgroundColor: "#000000",
      opacity: 100,
      textAlign: "center" as const,
      fontWeight: "normal" as const,
      fontStyle: "normal" as const,
      textDecoration: "none",
    },
    text: "Headline",
  },
  {
    name: "Caption",
    style: {
      fontFamily: "Roboto",
      fontSize: 36,
      color: "#000000",
      backgroundColor: "#FFFFFF",
      opacity: 100,
      textAlign: "center" as const,
      borderRadius: 15,
      fontWeight: "normal" as const,
      fontStyle: "normal" as const,
      textDecoration: "none",
    },
    text: "Text",
  },
  {
    name: "Quote",
    style: {
      fontFamily: "Abel",
      fontSize: 32,
      color: "#FFFFFF",
      backgroundColor: "#000000",
      opacity: 50,
      textAlign: "center" as const,
      borderRadius: 15,
      fontWeight: "normal" as const,
      fontStyle: "normal" as const,
      textDecoration: "none",
    },
    text: "Semi opaque",
  },
  {
    name: "Clean",
    style: {
      fontFamily: "Montserrat",
      fontSize: 32,
      color: "#000000",
      backgroundColor: "#000000",
      opacity: 0, // Transparent background
      textAlign: "center" as const,
      fontWeight: "bold" as const,
      fontStyle: "normal" as const,
      textDecoration: "none",
    },
    text: "No background",
  },
  {
    name: "Multi-line",
    style: {
      fontFamily: "Montserrat",
      fontSize: 32,
      color: "#000000",
      backgroundColor: "#FFFFFF",
      opacity: 100,
      textAlign: "center" as const,
      fontWeight: "bold" as const,
      fontStyle: "normal" as const,
      textDecoration: "none",
      useSvgFilter: true,
      borderRadius: 15,
    },
    text: "Multi-line\nText",
    previewImage: "/multiline.png",
  },
  {
    name: "Stroke",
    style: {
      fontFamily: "Roboto",
      fontSize: 32,
      color: "#FFFFFF",
      backgroundColor: "#FFFFFF",
      opacity: 0, // Transparent background
      strokeWidthRatio: 0.2, // 20% of font size
      strokeColor: "#000000", // Black stroke
      textAlign: "center" as const,
      fontWeight: "bold" as const,
      fontStyle: "normal" as const,
      textDecoration: "none",
      borderRadius: 4,
    },
    text: "Stroke",
  },
];

// Export the presets for use in other components
export { TEXT_PRESETS };

interface TextOverlayFormatControlsProps {
  textOverlay: TextOverlay;
  onChange: (overlay: TextOverlay) => void;
  isNewOverlay?: boolean;
  onDuplicate?: (id: string) => void;
  onDelete?: (id: string) => void;
  /** Which editor is active — affects default lineHeight for useSvgFilter */
  editorMode?: 'fabric' | 'remotion';
  brandColors?: BrandColorShortcut[];
  brandKitContext?: BrandKitColorPickerContext;
  /**
   * Reports when this panel drills into a sub-level (Edit Highlight) so the
   * host can retitle its shared header and route its back button to onBack.
   */
  onDrillDownChange?: (
    drillDown: { title: string; onBack: () => void } | null
  ) => void;
}

export function TextOverlayFormatControls({
  textOverlay,
  onChange,
  isNewOverlay = false,
  onDuplicate,
  onDelete,
  editorMode = 'fabric',
  brandColors,
  brandKitContext,
  onDrillDownChange,
}: TextOverlayFormatControlsProps) {
  const { activeWorkspaceId } = useAuth();
  const availableFonts = getAvailableFontsForUser(activeWorkspaceId || undefined);

  // Local state to track if the font size dropdown is open
  const [isFontSizeDropdownOpen, setIsFontSizeDropdownOpen] = useState(false);
  const fontSizeDropdownRef = useRef<HTMLDivElement>(null);

  // Helper function to update the text style
  const updateStyle = (styleUpdates: Partial<TextOverlay["style"]>) => {
    onChange({
      ...textOverlay,
      style: {
        ...textOverlay.style,
        ...styleUpdates,
      },
    });
  };

  // Handle preset selection - only apply certain style properties
  const handlePresetSelect = (presetName: string) => {
    const preset = TEXT_PRESETS.find((p) => p.name === presetName);
    if (!preset) return;

    const { fontFamily, fontSize, color, backgroundColor, ...applyableStyle } =
      preset.style;

    // Reset properties that might not be in the new preset
    const resetStyle = {
      ...applyableStyle,
      useSvgFilter: applyableStyle.useSvgFilter ?? false,
      strokeWidthRatio: applyableStyle.strokeWidthRatio ?? 0,
      strokeColor: applyableStyle.strokeColor ?? "#000000",
      borderRadius: applyableStyle.borderRadius ?? 0,
    };

    // Apply only the style properties that should be changed
    // Preserve current text, fontFamily, fontSize, color, and backgroundColor.
    // In highlight mode this targets the run; element-only keys (textAlign,
    // useSvgFilter) drop out in the run mapping.
    updateActiveStyle(resetStyle as Partial<TextOverlay["style"]>);
  };

  // --- Rich text runs (styled chips) ---
  const [selectedRunIndex, setSelectedRunIndex] = useState<number | null>(null);
  // A selection sent here via the field's "Style" button but not yet styled.
  // The chip only materializes on the first style change; backing out
  // untouched leaves no residue.
  const [pendingHighlight, setPendingHighlight] = useState<{
    start: number;
    end: number;
    text: string;
  } | null>(null);

  const selectedRun: TextRun | null =
    selectedRunIndex !== null &&
    textOverlay.runs?.[selectedRunIndex]?.style &&
    Object.keys(textOverlay.runs[selectedRunIndex].style!).length > 0
      ? textOverlay.runs[selectedRunIndex]
      : null;

  // What the drill-down edits: a real run, or the pending (unstyled) selection
  const activeRun: TextRun | null =
    selectedRun ?? (pendingHighlight ? { text: pendingHighlight.text, style: {} } : null);

  // Leave run mode when the overlay switches or the run disappears
  useEffect(() => {
    setSelectedRunIndex(null);
    setPendingHighlight(null);
  }, [textOverlay.id]);
  useEffect(() => {
    if (selectedRunIndex !== null && !selectedRun) setSelectedRunIndex(null);
  }, [selectedRunIndex, selectedRun]);

  // Leave run mode when the text changes from OUTSIDE this panel (e.g. undo):
  // a held pendingHighlight's offsets — or a run index — would point into the
  // old text and target the wrong words. The panel records the text of every
  // change it emits itself so those don't trip the reset.
  const emittedTextRef = useRef(textOverlay.text);
  useEffect(() => {
    if (textOverlay.text !== emittedTextRef.current) {
      emittedTextRef.current = textOverlay.text;
      setSelectedRunIndex(null);
      setPendingHighlight(null);
    }
  }, [textOverlay.text]);

  const handleStyleSelection = (range: { start: number; end: number; text: string }) => {
    setSelectedRunIndex(null);
    setPendingHighlight(range);
  };

  const emitRuns = (runs: TextRun[] | undefined) => {
    const next: TextOverlay = {
      ...textOverlay,
      text: runs ? runs.map((r) => r.text).join("") : textOverlay.text,
      runs,
    };
    if (!runs) delete (next as Partial<TextOverlay>).runs;
    emittedTextRef.current = next.text;
    onChange(next);
  };

  // Edits typed in the rich text field (plain text and chip deletions)
  const handleFieldChange = (value: { text: string; runs?: TextRun[] }) => {
    const next: TextOverlay = { ...textOverlay, text: value.text, runs: value.runs };
    if (!value.runs) delete (next as Partial<TextOverlay>).runs;
    emittedTextRef.current = next.text;
    onChange(next);
  };

  // The overlay's text as styled/unstyled segments (whole text when no runs)
  const currentSegments = (): TextRun[] =>
    textOverlay.runs?.length ? textOverlay.runs : [{ text: textOverlay.text }];

  const emitSegments = (segments: TextRun[]) => {
    const hasStyled = segments.some((r) => r.style && Object.keys(r.style).length > 0);
    if (hasStyled) {
      emitRuns(segments);
    } else {
      const next: TextOverlay = {
        ...textOverlay,
        text: segments.map((r) => r.text).join(""),
      };
      delete (next as Partial<TextOverlay>).runs;
      emittedTextRef.current = next.text;
      onChange(next);
    }
  };

  /**
   * Walk the segments and rewrite the unstyled one containing the pending
   * range [start, end). The field guarantees a pending selection never
   * overlaps an existing chip, so exactly one segment contains it.
   */
  const mapPendingSegment = (
    fn: (segmentText: string, s: number, e: number) => TextRun[]
  ): TextRun[] => {
    const { start, end } = pendingHighlight!;
    const out: TextRun[] = [];
    let pos = 0;
    for (const r of currentSegments()) {
      const rEnd = pos + r.text.length;
      const styled = r.style && Object.keys(r.style).length > 0;
      if (styled || rEnd <= start || pos >= end) {
        out.push(r);
      } else {
        out.push(...fn(r.text, start - pos, end - pos));
      }
      pos = rEnd;
    }
    return out;
  };

  // First style change turns the pending selection into a real run
  const materializePending = (style: TextRunStyle) => {
    if (!pendingHighlight) return;
    const out = mapPendingSegment((segText, s, e) => {
      const pieces: TextRun[] = [];
      if (s > 0) pieces.push({ text: segText.slice(0, s) });
      pieces.push({ text: segText.slice(s, e), style });
      if (e < segText.length) pieces.push({ text: segText.slice(e) });
      return pieces;
    });
    // The new run starts exactly at the pending range's start offset
    const newIndex = out.findIndex(
      (r, i) =>
        r.style &&
        Object.keys(r.style).length > 0 &&
        out.slice(0, i).reduce((n, x) => n + x.text.length, 0) === pendingHighlight.start
    );
    setPendingHighlight(null);
    setSelectedRunIndex(newIndex === -1 ? null : newIndex);
    emitRuns(out);
  };

  const updatePendingText = (text: string) => {
    if (!pendingHighlight) return;
    const { start } = pendingHighlight;
    const out = mapPendingSegment((segText, s, e) => [
      { text: segText.slice(0, s) + text + segText.slice(e) },
    ]);
    if (text.length === 0) {
      setPendingHighlight(null);
    } else {
      setPendingHighlight({ start, end: start + text.length, text });
    }
    emitSegments(out);
  };

  const updateActiveRunText = (text: string) => {
    if (pendingHighlight) {
      updatePendingText(text);
      return;
    }
    updateSelectedRunText(text);
  };

  const updateSelectedRunText = (text: string) => {
    if (selectedRunIndex === null || !textOverlay.runs) return;
    if (text.length === 0) {
      // Edited to empty = the run is gone (words and style)
      deleteSelectedRun();
      return;
    }
    emitRuns(
      textOverlay.runs.map((r, i) => (i === selectedRunIndex ? { ...r, text } : r))
    );
  };

  const setSelectedRunStyle = (patch: Partial<TextRunStyle>) => {
    if (selectedRunIndex === null || !textOverlay.runs) return;
    emitRuns(
      textOverlay.runs.map((r, i) => {
        if (i !== selectedRunIndex) return r;
        const style: TextRunStyle = { ...r.style };
        for (const [key, value] of Object.entries(patch)) {
          if (value === undefined) {
            delete style[key as keyof TextRunStyle];
          } else {
            (style as Record<string, unknown>)[key] = value;
          }
        }
        return { ...r, style };
      })
    );
  };

  // Remove styling, keep the words (chip dissolves into plain text)
  const dissolveActiveRun = () => {
    if (pendingHighlight) {
      // Nothing was styled yet — just leave the drill-down
      setPendingHighlight(null);
      return;
    }
    dissolveSelectedRun();
  };

  const dissolveSelectedRun = () => {
    if (selectedRunIndex === null || !textOverlay.runs) return;
    const raw = textOverlay.runs.map((r, i) =>
      i === selectedRunIndex ? { text: r.text } : r
    );
    setSelectedRunIndex(null);
    emitRuns(normalizeTextRuns(raw) ?? undefined);
  };

  // Remove the run entirely (words and style)
  const deleteActiveRun = () => {
    if (pendingHighlight) {
      const out = mapPendingSegment((segText, st, e) => [
        { text: segText.slice(0, st) + segText.slice(e) },
      ]);
      setPendingHighlight(null);
      emitSegments(out);
      return;
    }
    deleteSelectedRun();
  };

  const deleteSelectedRun = () => {
    if (selectedRunIndex === null || !textOverlay.runs) return;
    const raw = textOverlay.runs.filter((_, i) => i !== selectedRunIndex);
    setSelectedRunIndex(null);
    const normalized = normalizeTextRuns(raw);
    const next: TextOverlay = {
      ...textOverlay,
      text: raw.map((r) => r.text).join(""),
      runs: normalized,
    };
    if (!normalized) delete (next as Partial<TextOverlay>).runs;
    onChange(next);
  };

  const finishRunEditing = () => {
    setSelectedRunIndex(null);
    setPendingHighlight(null);
    if (textOverlay.runs) emitRuns(normalizeTextRuns(textOverlay.runs) ?? undefined);
  };

  // Let the host's shared header own the drill-down navigation (single
  // back button, retitled) instead of stacking a second nav level here
  const finishRunEditingRef = useRef(finishRunEditing);
  finishRunEditingRef.current = finishRunEditing;
  const inRunMode = activeRun !== null;
  useEffect(() => {
    if (!onDrillDownChange) return;
    onDrillDownChange(
      inRunMode
        ? { title: "Edit Highlight", onBack: () => finishRunEditingRef.current() }
        : null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inRunMode]);
  useEffect(() => {
    return () => onDrillDownChange?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The style controls target either the overlay or the selected run. In run
  // mode the shared controls show the run's EFFECTIVE style (run overrides
  // merged over the base), and the Background Color control acts as the
  // run's inline highlight.
  const activeStyle: TextOverlay["style"] = activeRun
    ? {
        ...textOverlay.style,
        fontFamily: activeRun.style?.fontFamily ?? textOverlay.style.fontFamily,
        fontSize: activeRun.style?.fontSize ?? textOverlay.style.fontSize,
        color: activeRun.style?.color ?? textOverlay.style.color,
        fontWeight: activeRun.style?.fontWeight ?? textOverlay.style.fontWeight,
        fontStyle: activeRun.style?.fontStyle ?? textOverlay.style.fontStyle,
        textDecoration:
          activeRun.style?.textDecoration ?? textOverlay.style.textDecoration,
        textTransform:
          activeRun.style?.textTransform ?? textOverlay.style.textTransform,
        // Background controls describe the run's highlight pill in run mode
        backgroundColor: activeRun.style?.highlightColor ?? "",
        opacity: activeRun.style?.opacity ?? 100,
        borderRadius: activeRun.style?.borderRadius ?? 0,
        // Stroke/shadow are inherited CSS — no run override means the base shows
        strokeWidthRatio:
          activeRun.style?.strokeWidthRatio ?? textOverlay.style.strokeWidthRatio,
        strokeColor: activeRun.style?.strokeColor ?? textOverlay.style.strokeColor,
        textShadow:
          activeRun.style?.textShadow === "none"
            ? undefined
            : activeRun.style?.textShadow ?? textOverlay.style.textShadow,
      }
    : textOverlay.style;

  const updateActiveStyle = (patch: Partial<TextOverlay["style"]>) => {
    if (!activeRun) {
      updateStyle(patch);
      return;
    }
    const runPatch: Partial<TextRunStyle> = {};
    if ("fontFamily" in patch) runPatch.fontFamily = patch.fontFamily || undefined;
    if ("fontSize" in patch) runPatch.fontSize = patch.fontSize;
    if ("color" in patch) runPatch.color = patch.color || undefined;
    if ("fontWeight" in patch)
      runPatch.fontWeight = patch.fontWeight === "bold" ? "bold" : "normal";
    if ("fontStyle" in patch)
      runPatch.fontStyle = patch.fontStyle === "italic" ? "italic" : "normal";
    if ("textDecoration" in patch)
      runPatch.textDecoration =
        patch.textDecoration === "underline" ? "underline" : "none";
    // "none" stays explicit so a run can undo an inherited base "uppercase"
    if ("textTransform" in patch)
      runPatch.textTransform =
        patch.textTransform === "uppercase" ? "uppercase" : "none";
    if ("backgroundColor" in patch)
      runPatch.highlightColor = patch.backgroundColor || undefined;
    if ("opacity" in patch) runPatch.opacity = patch.opacity;
    if ("borderRadius" in patch) runPatch.borderRadius = patch.borderRadius;
    if ("strokeWidthRatio" in patch) runPatch.strokeWidthRatio = patch.strokeWidthRatio;
    if ("strokeColor" in patch) runPatch.strokeColor = patch.strokeColor || undefined;
    // Disabling the shadow must be an explicit "none" — deleting the key
    // would fall back to the inherited base shadow instead
    if ("textShadow" in patch) runPatch.textShadow = patch.textShadow ?? "none";
    if (pendingHighlight) {
      const style: TextRunStyle = {};
      for (const [key, value] of Object.entries(runPatch)) {
        if (value !== undefined) (style as Record<string, unknown>)[key] = value;
      }
      if (Object.keys(style).length > 0) materializePending(style);
      return;
    }
    setSelectedRunStyle(runPatch);
  };

  // Handlers for font size adjustment (overlay or selected run)
  const increaseFontSize = () => {
    updateActiveStyle({ fontSize: activeStyle.fontSize + 1 });
  };

  const decreaseFontSize = () => {
    if (activeStyle.fontSize > 1) {
      updateActiveStyle({ fontSize: activeStyle.fontSize - 1 });
    }
  };

  const handleFontSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(e.target.value);
    if (!isNaN(newSize) && newSize > 0) {
      updateActiveStyle({ fontSize: newSize });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        fontSizeDropdownRef.current &&
        !fontSizeDropdownRef.current.contains(event.target as Node)
      ) {
        setIsFontSizeDropdownOpen(false);
      }
    }

    // Add event listener
    if (isFontSizeDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Clean up event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFontSizeDropdownOpen]);

  return (
    <div>
      {/* Inject the CSS to hide number input spinners */}
      <style>{hideNumberInputSpinners}</style>

      {/* Main container for all controls */}
      <div className="space-y-4 pr-1 pb-4">
        {/* Text Content — the same slot holds the overlay's full text (chips
            clickable to drill in) or, in highlight mode, just the highlight's
            words. Never both. */}
        <div className="space-y-2">
          <Label htmlFor={activeRun ? "run-text-content" : "text-content"}>
            Text Content
          </Label>
          {activeRun ? (
            <textarea
              id="run-text-content"
              value={activeRun.text}
              onChange={(e) => updateActiveRunText(e.target.value)}
              className="w-full p-2 min-h-[80px] border rounded-md resize-y"
              rows={3}
            />
          ) : (
            <RichTextField
              text={textOverlay.text}
              runs={textOverlay.runs}
              onChange={handleFieldChange}
              onRunSelect={setSelectedRunIndex}
              onStyleSelection={handleStyleSelection}
              selectedRunIndex={selectedRunIndex}
            />
          )}
        </div>

        {/* Preset Selector — applies to the highlight's run style in run mode */}
        <div className="space-y-2">
          <Label>Text Style Presets</Label>
          <Select onValueChange={handlePresetSelect}>
            <SelectTrigger className="h-10">
              <SelectValue placeholder="Choose a preset style..." />
            </SelectTrigger>
            <SelectContent className="z-[11000]">
              {TEXT_PRESETS.map((preset) => (
                <SelectItem key={preset.name} value={preset.name}>
                  <div className="flex items-center gap-2">
                    {preset.previewImage ? (
                      <img
                        src={preset.previewImage}
                        alt={preset.name}
                        className="w-6 h-4 object-cover rounded"
                      />
                    ) : (
                      <div
                        className="w-6 h-4 rounded text-[8px] flex items-center justify-center overflow-hidden"
                        style={{
                          backgroundColor:
                            (preset.style.opacity ?? 0) > 0
                              ? preset.style.backgroundColor
                              : "transparent",
                          color: preset.style.color,
                          fontFamily: preset.style.fontFamily,
                          fontWeight: preset.style.fontWeight,
                          fontStyle: preset.style.fontStyle,
                          textDecoration: preset.style.textDecoration,
                          textAlign: preset.style.textAlign,
                          opacity: (preset.style.opacity ?? 0) / 100,
                          borderRadius: preset.style.borderRadius
                            ? `${preset.style.borderRadius}px`
                            : "0px",
                          border:
                            preset.style.strokeWidthRatio &&
                            preset.style.strokeWidthRatio > 0
                              ? `1px solid ${preset.style.strokeColor || "#000000"}`
                              : "none",
                        }}
                      >
                        Aa
                      </div>
                    )}
                    <span>{preset.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Font Family and Font Size in the same row */}
        <div className="grid grid-cols-2 gap-6">
          {/* Font Family Selection */}
          <div className="space-y-2">
            <div className="flex items-center">
              <Label htmlFor="font-family">Font Family</Label>
            </div>
            <Select
              value={activeStyle.fontFamily}
              onValueChange={(value) => updateActiveStyle({ fontFamily: value })}
            >
              <SelectTrigger
                id="font-family"
                className="h-10 ring-offset-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                style={{
                  boxShadow: "none",
                  outline: "none",
                }}
              >
                <div className="w-full overflow-hidden whitespace-nowrap">
                  <SelectValue
                    className="text-left truncate block"
                    placeholder="Select a font"
                  />
                </div>
              </SelectTrigger>
              <SelectContent
                className="z-[11000] max-h-60"
                position="popper"
                sideOffset={4}
                align="start"
              >
                <div className="max-h-60 overflow-auto">
                  {availableFonts.map((font) => (
                    <SelectItem
                      key={font}
                      value={font}
                      style={{ fontFamily: font }}
                    >
                      {font}
                    </SelectItem>
                  ))}
                </div>
              </SelectContent>
            </Select>
          </div>

          {/* Font Size Control with buttons and dropdown */}
          <div className="space-y-2">
            <div className="flex items-center">
              <Label htmlFor="font-size">Font Size</Label>
            </div>
            <div className="relative flex h-10 w-full items-center">
              {/* Decrease Size Button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-10 w-10 rounded-r-none border-r-0 p-0"
                onClick={decreaseFontSize}
              >
                <Minus className="h-3 w-3" />
              </Button>

              {/* Current Size Display/Input - Click to show dropdown */}
              <div
                ref={fontSizeDropdownRef}
                className="relative flex-1 min-w-0"
              >
                <Input
                  id="font-size"
                  type="number"
                  value={activeStyle.fontSize}
                  onChange={handleFontSizeChange}
                  onClick={() =>
                    setIsFontSizeDropdownOpen(!isFontSizeDropdownOpen)
                  }
                  className="h-10 text-center rounded-none border-x-0 focus:ring-0 focus:ring-offset-0 cursor-pointer"
                  min="1"
                />

                {/* Dropdown Menu */}
                {isFontSizeDropdownOpen && (
                  <div className="absolute top-full left-0 w-full mt-1 max-h-52 overflow-auto rounded-md bg-popover text-popover-foreground shadow-lg z-[11000]">
                    <div className="py-1">
                      {FONT_SIZES.map((size) => (
                        <div
                          key={size}
                          className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground ${
                            activeStyle.fontSize === size
                              ? "bg-accent text-accent-foreground"
                              : ""
                          }`}
                          onClick={() => {
                            updateActiveStyle({ fontSize: size });
                            setIsFontSizeDropdownOpen(false);
                          }}
                        >
                          {size}px
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Increase Size Button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-10 w-10 rounded-l-none border-l-0 p-0"
                onClick={increaseFontSize}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>

        {/* Text Style: B/I/U/aA toggles + a single Canva-style alignment
            button that cycles left -> center -> right (element-level, hidden
            in run mode). One full-width row so nothing gets crowded. */}
        <div className="space-y-2">
          <div className="flex items-center">
            <Label>Text Style</Label>
          </div>
          <div className="flex items-center gap-2">
            <ToggleGroup
              type="multiple"
              value={[
                activeStyle.fontWeight === "bold" ? "bold" : undefined,
                activeStyle.fontStyle === "italic" ? "italic" : undefined,
                activeStyle.textDecoration === "underline"
                  ? "underline"
                  : undefined,
                activeStyle.textTransform === "uppercase"
                  ? "uppercase"
                  : undefined,
              ].filter((v): v is string => v !== undefined)}
              onValueChange={(value: string[]) => {
                updateActiveStyle({
                  fontWeight: value.includes("bold") ? "bold" : "normal",
                  fontStyle: value.includes("italic") ? "italic" : "normal",
                  textDecoration: value.includes("underline")
                    ? "underline"
                    : "none",
                  textTransform: value.includes("uppercase")
                    ? "uppercase"
                    : "none",
                });
              }}
              className="flex flex-1 h-10"
            >
              <ToggleGroupItem
                value="bold"
                aria-label="Toggle bold"
                className="flex-1"
              >
                <Bold className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem
                value="italic"
                aria-label="Toggle italic"
                className="flex-1"
              >
                <Italic className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem
                value="underline"
                aria-label="Toggle underline"
                className="flex-1"
              >
                <Underline className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem
                value="uppercase"
                aria-label="Toggle uppercase"
                className="flex-1 px-1"
              >
                <span className="text-sm font-medium leading-none">aA</span>
              </ToggleGroupItem>
            </ToggleGroup>
            {!activeRun && (
              <Button
                type="button"
                variant="outline"
                aria-label={`Text alignment: ${activeStyle.textAlign}. Click to change`}
                title={`Align ${activeStyle.textAlign} — click to change`}
                className="h-10 w-12 p-0 shrink-0"
                onClick={() => {
                  const next =
                    activeStyle.textAlign === "left"
                      ? "center"
                      : activeStyle.textAlign === "center"
                        ? "right"
                        : "left";
                  updateActiveStyle({ textAlign: next });
                }}
              >
                {activeStyle.textAlign === "center" ? (
                  <AlignCenter className="h-4 w-4" />
                ) : activeStyle.textAlign === "right" ? (
                  <AlignRight className="h-4 w-4" />
                ) : (
                  <AlignLeft className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Text Color and Background Color in the same row */}
        <div className="grid grid-cols-2 gap-6">
          {/* Text Color */}
          <div className="space-y-2">
            <div className="flex items-center">
              <Label htmlFor="text-color">Text Color</Label>
            </div>
            <div className="flex">
              <BrandColorPickerSwatch
                id="text-color"
                value={activeStyle.color}
                onChange={(color) => updateActiveStyle({ color })}
                className="h-10 w-10"
                brandColors={brandColors}
                brandKitContext={brandKitContext}
                ariaLabel="Choose text color"
              />
              <HexColorInput
                value={activeStyle.color}
                onChange={(color) => updateActiveStyle({ color })}
                allowEmpty={inRunMode}
                className="w-full ml-2"
                aria-label="Text color hex"
              />
            </div>
          </div>

          {/* Background Color — targets the run's inline highlight in run mode */}
          <div className="space-y-2">
            <div className="flex items-center">
              <Label htmlFor="bg-color">Background Color</Label>
            </div>
            <div className="flex">
              <BrandColorPickerSwatch
                id="bg-color"
                value={activeStyle.backgroundColor}
                onChange={(color) => updateActiveStyle({ backgroundColor: color })}
                className="h-10 w-10"
                brandColors={brandColors}
                brandKitContext={brandKitContext}
                ariaLabel="Choose text background color"
              />
              <HexColorInput
                value={activeStyle.backgroundColor}
                onChange={(backgroundColor) =>
                  updateActiveStyle({ backgroundColor })
                }
                allowEmpty={inRunMode}
                className="w-full ml-2"
                aria-label="Text background color hex"
              />
            </div>
          </div>
        </div>

        {/* Background Opacity (run mode: highlight pill opacity) */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between">
            <Label
              htmlFor="bg-opacity"
              className={
                !!activeStyle.useSvgFilter ? "text-muted-foreground" : ""
              }
            >
              Opacity
            </Label>
            <span
              className={`text-sm ${
                !!activeStyle.useSvgFilter ? "text-muted-foreground" : ""
              }`}
            >
              {activeStyle.opacity}%
            </span>
          </div>
          <Slider
            id="bg-opacity"
            min={0}
            max={100}
            step={1}
            value={[activeStyle.opacity]}
            onValueChange={(value: number[]) => {
              updateActiveStyle({ opacity: value[0] });
            }}
            className="[&>span:first-child]:h-2 [&>span:first-child>span]:h-2"
            disabled={!!activeStyle.useSvgFilter}
          />
        </div>

        {/* Border Radius and Line Height in the same row */}
        <div className="grid grid-cols-2 gap-6">
          {/* Border Radius Control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="border-radius">Border Radius</Label>
              <span className="text-sm">
                {activeStyle.borderRadius || 0}
              </span>
            </div>
            <Slider
              id="border-radius"
              min={0}
              max={50}
              step={1}
              value={[activeStyle.borderRadius || 0]}
              onValueChange={(value: number[]) => {
                updateActiveStyle({ borderRadius: value[0] });
              }}
              className="[&>span:first-child]:h-2 [&>span:first-child>span]:h-2"
            />
          </div>

          {/* Line Height Control (line-level, hidden in run mode) */}
          {!activeRun && (
          <div className="space-y-2">
            <div className="flex items-center">
              <Label htmlFor="line-height">Line Height</Label>
            </div>
            {(() => {
              // Default lineHeight depends on useSvgFilter for backward compatibility
              const defaultLineHeight = activeStyle.useSvgFilter
                ? (editorMode === 'remotion' ? 1.4 : 1.0)
                : 1.4;
              const currentLineHeight = activeStyle.lineHeight ?? defaultLineHeight;
              return (
                <div className="flex h-10 w-full items-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-10 w-10 rounded-r-none border-r-0 p-0"
                    onClick={() => {
                      if (currentLineHeight > 0.8) {
                        updateActiveStyle({ lineHeight: Math.round((currentLineHeight - 0.1) * 10) / 10 });
                      }
                    }}
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <Input
                    id="line-height"
                    type="number"
                    value={currentLineHeight.toFixed(1)}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value) && value >= 0.5 && value <= 3) {
                        updateActiveStyle({ lineHeight: value });
                      }
                    }}
                    className="h-10 text-center rounded-none border-x-0 focus:ring-0 focus:ring-offset-0"
                    min="0.5"
                    max="3"
                    step="0.1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-10 w-10 rounded-l-none border-l-0 p-0"
                    onClick={() => {
                      if (currentLineHeight < 3) {
                        updateActiveStyle({ lineHeight: Math.round((currentLineHeight + 0.1) * 10) / 10 });
                      }
                    }}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              );
            })()}
          </div>
          )}
        </div>

        {/* --- Text Stroke Controls (Inline with other controls) --- */}
        {/* Grid for Width Slider and Color Picker (no border/padding/group label) */}
        <div className="grid grid-cols-2 gap-6 pt-4">
          {/* Stroke Width Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="stroke-width">Stroke Width</Label>
              <span className="text-sm">
                {/* Display as percentage, round for neatness */}
                {`${Math.round(
                  (activeStyle.strokeWidthRatio || 0) * 100
                )}%`}
              </span>
            </div>
            <Slider
              id="stroke-width"
              min={0} // Min ratio 0 (off)
              max={0.5} // Max ratio 0.5 (50% of font size)
              step={0.01} // Step 1%
              value={[activeStyle.strokeWidthRatio || 0]}
              onValueChange={(value: number[]) => {
                updateActiveStyle({ strokeWidthRatio: value[0] });
              }}
              className="[&>span:first-child]:h-2 [&>span:first-child>span]:h-2"
            />
          </div>

          {/* Stroke Color Picker */}
          <div className="space-y-2">
            <div className="flex items-center">
              <Label htmlFor="stroke-color">Stroke Color</Label>
            </div>
            <div className="flex">
              <BrandColorPickerSwatch
                id="stroke-color"
                value={activeStyle.strokeColor || "#000000"} // Default to black if undefined
                onChange={(color) => updateActiveStyle({ strokeColor: color })}
                className="h-10 w-10"
                brandColors={brandColors}
                brandKitContext={brandKitContext}
                ariaLabel="Choose text stroke color"
                disabled={(activeStyle.strokeWidthRatio || 0) === 0}
              />
              <HexColorInput
                value={activeStyle.strokeColor || "#000000"}
                onChange={(strokeColor) => updateActiveStyle({ strokeColor })}
                className="w-full ml-2"
                aria-label="Text stroke color hex"
                // Disable if stroke width is 0
                disabled={(activeStyle.strokeWidthRatio || 0) === 0}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Text Shadow (run mode: shadow override for the run) */}
      <div className="space-y-3 pt-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Text Shadow</Label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!activeStyle.textShadow}
              onChange={(e) => {
                if (e.target.checked) {
                  updateActiveStyle({ textShadow: "2px 2px 4px rgba(0,0,0,0.5)" });
                } else {
                  updateActiveStyle({ textShadow: undefined });
                }
              }}
              className="rounded border-gray-300"
            />
            <span className="text-xs text-muted-foreground">Enable</span>
          </label>
        </div>
        {activeStyle.textShadow && (() => {
          // Parse current shadow: "Xpx Ypx Bpx rgba(r,g,b,a)" or "Xpx Ypx Bpx #color"
          const parts = activeStyle.textShadow.match(
            /^(-?[\d.]+)px\s+(-?[\d.]+)px\s+([\d.]+)px\s+(.+)$/
          );
          const offsetX = parts ? parseFloat(parts[1]) : 2;
          const offsetY = parts ? parseFloat(parts[2]) : 2;
          const blur = parts ? parseFloat(parts[3]) : 4;
          const colorStr = parts ? parts[4] : "rgba(0,0,0,0.5)";

          // Extract hex color and opacity from rgba or hex
          let hexColor = "#000000";
          let opacity = 0.5;
          const rgbaMatch = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
          if (rgbaMatch) {
            const r = parseInt(rgbaMatch[1]);
            const g = parseInt(rgbaMatch[2]);
            const b = parseInt(rgbaMatch[3]);
            hexColor = `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
            opacity = rgbaMatch[4] ? parseFloat(rgbaMatch[4]) : 1;
          } else if (colorStr.startsWith("#")) {
            hexColor = colorStr;
            opacity = 1;
          }

          const rebuildShadow = (x: number, y: number, b: number, hex: string, op: number) => {
            const r2 = parseInt(hex.slice(1, 3), 16);
            const g2 = parseInt(hex.slice(3, 5), 16);
            const b2 = parseInt(hex.slice(5, 7), 16);
            return `${x}px ${y}px ${b}px rgba(${r2},${g2},${b2},${op})`;
          };

          return (
            <div className="space-y-2 pl-2">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">X</Label>
                  <Input
                    type="number"
                    value={offsetX}
                    onChange={(e) => updateActiveStyle({ textShadow: rebuildShadow(parseFloat(e.target.value) || 0, offsetY, blur, hexColor, opacity) })}
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs">Y</Label>
                  <Input
                    type="number"
                    value={offsetY}
                    onChange={(e) => updateActiveStyle({ textShadow: rebuildShadow(offsetX, parseFloat(e.target.value) || 0, blur, hexColor, opacity) })}
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs">Blur</Label>
                  <Input
                    type="number"
                    value={blur}
                    min={0}
                    onChange={(e) => updateActiveStyle({ textShadow: rebuildShadow(offsetX, offsetY, parseFloat(e.target.value) || 0, hexColor, opacity) })}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs w-12">Color</Label>
                <BrandColorPickerSwatch
                  value={hexColor}
                  onChange={(color) =>
                    updateActiveStyle({
                      textShadow: rebuildShadow(
                        offsetX,
                        offsetY,
                        blur,
                        color,
                        opacity
                      ),
                    })
                  }
                  className="h-8 w-8"
                  brandColors={brandColors}
                  brandKitContext={brandKitContext}
                  ariaLabel="Choose text shadow color"
                />
                <div className="flex-1">
                  <Slider
                    min={0}
                    max={100}
                    step={5}
                    value={[Math.round(opacity * 100)]}
                    onValueChange={(v) => updateActiveStyle({ textShadow: rebuildShadow(offsetX, offsetY, blur, hexColor, v[0] / 100) })}
                    className="[&>span:first-child]:h-1.5 [&>span:first-child>span]:h-1.5"
                  />
                </div>
                <span className="text-xs w-8 text-right">{Math.round(opacity * 100)}%</span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Action Buttons — highlight mode mirrors the overlay's action slot */}
      <div className="pt-4 border-t space-y-2 flex-shrink-0 bg-background pb-2 pr-1">
        {activeRun ? (
          <>
            <Button
              variant="outline"
              className="w-full flex items-center justify-center gap-2"
              onClick={dissolveActiveRun}
            >
              Remove styling
            </Button>
            <Button
              variant="outline"
              className="w-full flex items-center justify-center gap-2 border-red-500 text-red-500 hover:bg-red-50 hover:text-red-700 focus-visible:ring-red-400"
              onClick={deleteActiveRun}
            >
              <Trash2 size={16} />
              Delete text
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              className="w-full flex items-center justify-center gap-2"
              onClick={() => {
                if (onDuplicate && textOverlay) {
                  onDuplicate(textOverlay.id);
                }
              }}
              disabled={!onDuplicate || !textOverlay}
            >
              <Copy size={16} />
              Duplicate
            </Button>
            <Button
              variant="outline"
              className="w-full flex items-center justify-center gap-2 border-red-500 text-red-500 hover:bg-red-50 hover:text-red-700 focus-visible:ring-red-400"
              onClick={() => {
                if (onDelete && textOverlay) {
                  onDelete(textOverlay.id);
                }
              }}
              disabled={!onDelete || !textOverlay}
            >
              <Trash2 size={16} />
              Delete
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
